import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { RefreshCw, Plus } from "lucide-react";
import { cn } from "@/lib/utils";
import BookingDetailDialog, { formatOrderNumber, type BookingDetail, type BookingStatus } from "./BookingDetailDialog";
import BookingCreateDialog from "./BookingCreateDialog";
import BookingsAgenda from "./BookingsAgenda";
import BookingsNotifications from "./BookingsNotifications";
import { STATUS_LABEL, STATUS_ROW_CLASS } from "./bookingsShared";

type Booking = BookingDetail;

const TABS: ("all" | BookingStatus)[] = ["all", "pending", "confirmed", "completed", "cancelled"];

const AdminBookings = () => {
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<"table" | "agenda">("table");
  const [filter, setFilter] = useState<"all" | BookingStatus>("all");
  const [selected, setSelected] = useState<Booking | null>(null);
  const [confirming, setConfirming] = useState<Booking | null>(null);
  const [creating, setCreating] = useState(false);
  const [highlightId, setHighlightId] = useState<string | null>(null);
  const rowRefs = useRef<Record<string, HTMLTableRowElement | null>>({});

  const load = useCallback(async () => {
    setLoading(true);
    const { data } = await supabase.from("bookings").select("*").order("created_at", { ascending: false });
    setBookings((data as Booking[]) || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const unreadIds = useMemo(
    () => new Set(bookings.filter((b) => b.status === "pending").map((b) => b.id)),
    [bookings]
  );

  const goToBooking = useCallback((b: Booking) => {
    setMode("table");
    setFilter("all");
    setHighlightId(b.id);
    setSelected(b);
  }, []);

  useEffect(() => {
    if (!highlightId || selected) return;
    const row = rowRefs.current[highlightId];
    row?.scrollIntoView({ behavior: "smooth", block: "center" });
    const t = setTimeout(() => setHighlightId(null), 3000);
    return () => clearTimeout(t);
  }, [highlightId, selected]);

  const updateStatus = async (id: string, status: BookingStatus) => {
    if (status === "confirmed") {
      const booking = bookings.find((b) => b.id === id);
      if (booking && booking.transfer_price == null) {
        setConfirming(booking);
        toast.info("Saisissez le prix du transfert pour confirmer cette réservation.");
        return;
      }
    }
    const { error } = await supabase.from("bookings").update({ status }).eq("id", id);
    if (error) toast.error("Erreur: " + error.message);
    else { toast.success("Statut mis à jour"); load(); }
  };

  const counts = useMemo(() => ({
    all: bookings.length,
    pending: bookings.filter((b) => b.status === "pending").length,
    confirmed: bookings.filter((b) => b.status === "confirmed").length,
    completed: bookings.filter((b) => b.status === "completed").length,
    cancelled: bookings.filter((b) => b.status === "cancelled").length,
  }), [bookings]);

  const filtered = useMemo(
    () => (filter === "all" ? bookings : bookings.filter((b) => b.status === filter)),
    [bookings, filter]
  );

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="font-heading">Réservations</CardTitle>
        <div className="flex flex-wrap items-center gap-2">
          <BookingsNotifications bookings={bookings} onSelect={goToBooking} onRefresh={load} />
          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle réservation
          </Button>
          <Button variant="outline" size="sm" onClick={load} disabled={loading}>
            <RefreshCw className={cn("w-4 h-4 mr-2", loading && "animate-spin")} />
            Actualiser
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <Tabs value={mode} onValueChange={(v) => setMode(v as typeof mode)}>
          <TabsList>
            <TabsTrigger value="table" className="text-xs px-4">Tableau</TabsTrigger>
            <TabsTrigger value="agenda" className="text-xs px-4">Agenda</TabsTrigger>
          </TabsList>
        </Tabs>

        {mode === "agenda" ? (
          <BookingsAgenda bookings={bookings} unreadIds={unreadIds} onSelect={(b) => setSelected(b)} />
        ) : (
          <>
            <Tabs value={filter} onValueChange={(v) => setFilter(v as typeof filter)}>
              <TabsList className="flex-wrap h-auto gap-1">
                {TABS.map((tab) => (
                  <TabsTrigger key={tab} value={tab} className="text-xs px-3 py-1.5">
                    {tab === "all" ? "Tous" : STATUS_LABEL[tab]}
                    <span className="ml-1.5 rounded-full bg-background px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground border border-border">
                      {counts[tab]}
                    </span>
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>

            {filtered.length === 0 ? (
              <p className="text-muted-foreground font-body text-center py-8">
                {filter === "all" ? "Aucune réservation." : `Aucune réservation ${STATUS_LABEL[filter].toLowerCase()}.`}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>N°</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Nom</TableHead>
                      <TableHead>Tél</TableHead>
                      <TableHead>Service</TableHead>
                      <TableHead>Trajet</TableHead>
                      <TableHead>Pax</TableHead>
                      <TableHead>Prix</TableHead>
                      <TableHead>Date souhaitée</TableHead>
                      <TableHead>Chauffeur</TableHead>
                      <TableHead>Statut</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.map((b) => (
                      <TableRow
                        key={b.id}
                        ref={(el) => { rowRefs.current[b.id] = el; }}
                        onClick={() => setSelected(b)}
                        className={cn(
                          STATUS_ROW_CLASS[b.status],
                          "transition-colors cursor-pointer",
                          highlightId === b.id && "ring-2 ring-primary ring-inset"
                        )}
                      >
                        <TableCell className="font-body text-sm font-semibold whitespace-nowrap">
                          <span className="flex items-center gap-1.5">
                            {unreadIds.has(b.id) && <span className="w-2 h-2 rounded-full bg-destructive shrink-0" />}
                            {formatOrderNumber(b.order_number)}
                          </span>
                        </TableCell>
                        <TableCell className="font-body text-sm">{new Date(b.created_at).toLocaleDateString("fr-FR")}</TableCell>
                        <TableCell className="font-body font-medium">{b.name}</TableCell>
                        <TableCell className="font-body text-sm">{b.phone}</TableCell>
                        <TableCell className="font-body text-sm">{b.service}</TableCell>
                        <TableCell className="font-body text-sm">
                          {b.pickup_location || b.dropoff_location
                            ? `${b.pickup_location || "?"} → ${b.dropoff_location || "?"}`
                            : "—"}
                        </TableCell>
                        <TableCell className="font-body text-sm">{b.passengers ?? "—"}</TableCell>
                        <TableCell className="font-body text-sm whitespace-nowrap">
                          {b.transfer_price != null ? `${b.transfer_price} MAD` : "—"}
                        </TableCell>
                        <TableCell className="font-body text-sm">{b.booking_date || "—"}</TableCell>
                        <TableCell className="font-body text-sm">{b.driver_phone || b.driver_name || "—"}</TableCell>
                        <TableCell onClick={(e) => e.stopPropagation()}>
                          <Select key={b.status} defaultValue={b.status} onValueChange={(val) => updateStatus(b.id, val as BookingStatus)}>
                            <SelectTrigger className="w-[130px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">En attente</SelectItem>
                              <SelectItem value="confirmed">Confirmé</SelectItem>
                              <SelectItem value="completed">Terminé</SelectItem>
                              <SelectItem value="cancelled">Annulé</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </>
        )}

        <BookingDetailDialog
          booking={selected ?? confirming}
          requirePrice={!!confirming}
          onClose={() => {
            setSelected(null);
            setConfirming(null);
          }}
          onSaved={() => {
            load();
            setSelected(null);
            setConfirming(null);
          }}
        />

        <BookingCreateDialog
          open={creating}
          onClose={() => setCreating(false)}
          onCreated={() => {
            setCreating(false);
            load();
          }}
        />
      </CardContent>
    </Card>
  );
};

export default AdminBookings;
